import { TFunction } from 'i18next';
import { EntityTypes } from 'models/entity';
import { AuditCriteriaLayer } from 'models/auditCriteriaGeneric';
import type { AICriterionOutcome } from 'models/auditCriteriaGenericSets';

export const auditCriterionModeRecurring = 'Recurring';
export const auditCriterionModeOneTime = 'OneTime';

export const auditCriterionVerificationAI = 'AI';
export const auditCriterionVerificationManual = 'Manual';
export const auditCriterionVerificationIntegration = 'Integration';

// The evidence-inspection status the AI leaves on a check, as it travels on the wire: the raw enum
// name from the API (EvidenceInspectionStatus.ToString()). It is a CONTRACT value - the take-over
// and the worst-status pickers match on these names - so it is never localized in the model, only
// on its way to the screen.
export const auditCheckStatusSatisfied = 'Satisfied';
export const auditCheckStatusPartiallySatisfied = 'PartiallySatisfied';
export const auditCheckStatusNotSatisfied = 'NotSatisfied';
export const auditCheckStatusContradicted = 'Contradicted';
export const auditCheckStatusNotApplicable = 'NotApplicable';
export const auditCheckStatusNotInspectable = 'NotInspectable';
export const auditCheckStatusUnknown = 'Unknown';

// The plain-language text of that status. Never show the raw name: "PartiallySatisfied" is a
// contract value, not language an auditor should have to read.
// Literal keys on purpose: the unused-translations test scans for them.
export const getAuditCheckStatusText = (status: string | undefined, t: TFunction<string[]>): string | undefined => {
  switch (status) {
    case auditCheckStatusSatisfied:
      return t('criteria:EvidenceStatus.Satisfied');
    case auditCheckStatusPartiallySatisfied:
      return t('criteria:EvidenceStatus.PartiallySatisfied');
    case auditCheckStatusNotSatisfied:
      return t('criteria:EvidenceStatus.NotSatisfied');
    case auditCheckStatusContradicted:
      return t('criteria:EvidenceStatus.Contradicted');
    case auditCheckStatusNotApplicable:
      return t('criteria:EvidenceStatus.NotApplicable');
    case auditCheckStatusNotInspectable:
      return t('criteria:EvidenceStatus.NotInspectable');
    case auditCheckStatusUnknown:
      return t('criteria:EvidenceStatus.Unknown');
    default:
      return undefined;
  }
};

// Whether the pass/fail answer the AI wrote beside the human one already says everything the status
// does. Satisfied and NotSatisfied are exactly yes and no; every other status carries something the
// answer cannot hold - partly met, contradicted, could not be checked - and has to be said as well.
export const isAuditCheckStatusImpliedByAnswer = (status: string | undefined): boolean =>
  status === auditCheckStatusSatisfied || status === auditCheckStatusNotSatisfied;

// Why an AI audit left no verdict on a criterion. Travels in the same wire field as the status above
// (the AI result comment), which is why the dot prefix matters: it is what keeps a reason from ever
// being read as a status. Mirrors the server's CriterionAiOutcomeCodes - a cross-repo contract, so a
// value may be added but never renamed.
export const auditCriterionNotAuditedManual = 'NotAudited.Manual';
export const auditCriterionNotAuditedNoEvidence = 'NotAudited.NoEvidence';
export const auditCriterionNotAuditedNotInspected = 'NotAudited.NotInspected';
export const auditCriterionNotAuditedEvidenceTaskOpen = 'NotAudited.EvidenceTaskOpen';
export const auditCriterionNotAuditedNoCheckRuns = 'NotAudited.NoCheckRuns';

// The reason in plain language, or undefined when the code is a status rather than a reason. The
// mirror of getAuditCheckStatusText, and the two are exclusive: a criterion carries one or the other.
// Literal keys on purpose: the unused-translations test scans for them.
export const getAuditCriterionNotAuditedText = (
  code: string | undefined,
  t: TFunction<string[]>,
): string | undefined => {
  switch (code) {
    case auditCriterionNotAuditedManual:
      return t('criteria:NotAudited.Manual');
    case auditCriterionNotAuditedNoEvidence:
      return t('criteria:NotAudited.NoEvidence');
    case auditCriterionNotAuditedNotInspected:
      return t('criteria:NotAudited.NotInspected');
    case auditCriterionNotAuditedEvidenceTaskOpen:
      return t('criteria:NotAudited.EvidenceTaskOpen');
    case auditCriterionNotAuditedNoCheckRuns:
      return t('criteria:NotAudited.NoCheckRuns');
    default:
      return undefined;
  }
};

// Whether an evidence task proves its one-time criterion: a person completed or archived the task.
// The AI advice on the task does not count; the server applies the same rule in the stats.
export const isEvidenceTaskVerified = (binding: { taskClosed?: boolean }): boolean => binding.taskClosed === true;

// Why a criteria set must be re-evaluated. Mirrors the server's AuditCriteriaReviewReasons - a
// cross-repo contract, so a value may be added but never renamed.
export const auditCriteriaReviewReasonStandardActivated = 'Standard.Activated';
export const auditCriteriaReviewReasonStandardDeactivated = 'Standard.Deactivated';
export const auditCriteriaReviewReasonStandardRemoved = 'Standard.Removed';
export const auditCriteriaReviewReasonStandardUpgraded = 'Standard.Upgraded';
export const auditCriteriaReviewReasonRequirementLinkChanged = 'Requirement.LinkChanged';
export const auditCriteriaReviewReasonTextChanged = 'Text.Changed';
export const auditCriteriaReviewReasonDocumentLinkChanged = 'Document.LinkChanged';
export const auditCriteriaReviewReasonStandardsUnspecified = 'Standards.Unspecified';
export const auditCriteriaReviewReasonDocumentChanged = 'Document.Changed';
// Not a flag reason: the review summary's reason for a set the AI re-evaluated and changed, which
// nobody reviewed since.
export const auditCriteriaReviewReasonReevaluated = 'Reevaluated';

// The reason in plain language. Literal keys on purpose: the unused-translations test scans for them.
export const getAuditCriteriaReviewReasonText = (code: string | undefined, t: TFunction<string[]>): string => {
  switch (code) {
    case auditCriteriaReviewReasonStandardActivated:
      return t('criteria:ReviewReason.StandardActivated');
    case auditCriteriaReviewReasonStandardDeactivated:
      return t('criteria:ReviewReason.StandardDeactivated');
    case auditCriteriaReviewReasonStandardRemoved:
      return t('criteria:ReviewReason.StandardRemoved');
    case auditCriteriaReviewReasonStandardUpgraded:
      return t('criteria:ReviewReason.StandardUpgraded');
    case auditCriteriaReviewReasonRequirementLinkChanged:
      return t('criteria:ReviewReason.RequirementLinkChanged');
    case auditCriteriaReviewReasonTextChanged:
      return t('criteria:ReviewReason.TextChanged');
    case auditCriteriaReviewReasonDocumentLinkChanged:
      return t('criteria:ReviewReason.DocumentLinkChanged');
    case auditCriteriaReviewReasonStandardsUnspecified:
      return t('criteria:ReviewReason.StandardsUnspecified');
    case auditCriteriaReviewReasonDocumentChanged:
      return t('criteria:ReviewReason.DocumentChanged');
    case auditCriteriaReviewReasonReevaluated:
      return t('criteria:ReviewReason.Reevaluated');
    default:
      return t('criteria:ReviewReason.Unknown');
  }
};

// What a re-evaluation did to a criterion since the set's last review. Server-owned, read-only.
export const auditCriterionChangeKindNew = 'New';
export const auditCriterionChangeKindReworded = 'Reworded';
export const auditCriterionChangeKindArchived = 'Archived';

// The state of a criterion, as it travels on the wire (the server's AuditCriterionState names). Only an
// Active criterion is asked: the audits, the procedure forms, the stats and the evidence wizard. A person
// sets NotApplicable when the obligation does not apply to the organization; Archived is retired.
export const auditCriterionStateActive = 'Active';
export const auditCriterionStateNotApplicable = 'NotApplicable';
export const auditCriterionStateArchived = 'Archived';

// The state in plain language. Literal keys on purpose: the unused-translations test scans for them.
export const getAuditCriterionStateText = (state: string | undefined, t: TFunction<string[]>): string => {
  switch (state) {
    case auditCriterionStateNotApplicable:
      return t('criteria:State.NotApplicable');
    case auditCriterionStateArchived:
      return t('criteria:State.Archived');
    default:
      return t('criteria:State.Active');
  }
};

// Sorted, distinct, positive: the one shape a standards list is compared in.
export const normalizeNormIds = (normIds: number[] | undefined): number[] =>
  [...new Set((normIds ?? []).filter((id) => id > 0))].sort((a, b) => a - b);

export const areNormIdsEqual = (left: number[] | undefined, right: number[] | undefined): boolean => {
  const a = normalizeNormIds(left);
  const b = normalizeNormIds(right);

  return a.length === b.length && a.every((id, index) => id === b[index]);
};

// The one-time state of a criterion an item reads from a generic set (PLAN-075 Task 30, spec 5.4): Verified after a
// pass with the required comment or attachment within the certification cycle, Pending while an assessment task of
// the item is open, Missing otherwise.
export type AuditCriterionOneTimeState = 'Verified' | 'Pending' | 'Missing';

export class AuditCriterion {
  // The criterion's row. Server-owned, like the trails and the stamps below; 0 before the first save.
  auditCriterionId: number;

  criterionKey: string;

  // First criterionKey of the criterion's lineage, stable across every version of it. Server-owned:
  // a material edit forks the criterion to a new criterionKey, this key is what survives the fork.
  lineageKey: string;

  requirement: string;

  successCriteria: string;

  required: boolean;

  mode: string;

  verification: string;

  sourceLinkId: number | undefined;

  sourceName: string;

  // Which generation input the criterion came from: 'Document', 'Control' (the entity's own
  // definition text), 'Implementation' or 'Standard'. Empty for legacy and manually added
  // criteria; the UI then falls back to its link/quote-based source labeling.
  sourceKind: string;

  sourceText: string;

  conflictNote: string;

  // When this criterion was generated. Per criterion, unlike the set-level generatedOn. Server-owned.
  generatedOn: Date | undefined;

  // Set when the criterion was archived: out of the active set, but its history and its
  // evidence-task bindings are kept. Undefined while the criterion is active. Server-owned.
  archivedOn: Date | undefined;

  archivedById: string | undefined;

  requiresComment: boolean;

  requiresEvidence: boolean;

  // The standards (tenant norm ids) the criterion is explicitly tagged with. Undefined = unrestricted
  // (it applies under every standard) AND "not sent" on a save, so a legacy criterion is never
  // converted by a save that did not touch it. A control criterion is saved with at least one.
  normIds: number[] | undefined;

  // Read-only: the standards the criterion effectively belongs to - its explicit ones minus the stale
  // ones, or every standard linked to the entity when it has none. What the editor shows and pre-fills.
  effectiveNormIds: number[];

  evidenceTasks: EvidenceTaskBinding[];

  // Read-only: what a re-evaluation did to this criterion since the set's last review
  // (auditCriterionChangeKind*), undefined when nothing. Never sent on a save.
  changeKind: string | undefined;

  // Read-only: the one-time state on an item that reads a generic set (AuditCriterionOneTimeState); undefined on an
  // item with criteria of its own, on a generic set's own row and for a recurring criterion. Never sent on a save.
  oneTimeState: AuditCriterionOneTimeState | undefined;

  // auditCriterionState*. Changed through the state route only, never by a save.
  state: string;

  // The organization's own reference for the criterion (a control framework id, a line in an audit
  // program). Written by a save; matched on by nothing.
  externalId: string;

  // The short header of the criterion's column in the registers (PLAN-CTI-082), at most 64 characters; empty
  // shows the start of the requirement (getCriterionColumnTitle).
  columnName: string;

  // The criterion's own comment and change history.
  commentTrailId: number;

  auditTrailId: number;

  created: Date | undefined;

  createdById: string | undefined;

  modified: Date | undefined;

  modifiedById: string | undefined;

  constructor() {
    this.auditCriterionId = 0;
    this.criterionKey = '';
    this.lineageKey = '';
    this.requirement = '';
    this.successCriteria = '';
    this.required = true;
    this.mode = auditCriterionModeRecurring;
    this.verification = auditCriterionVerificationAI;
    this.sourceLinkId = undefined;
    this.sourceName = '';
    this.sourceKind = '';
    this.sourceText = '';
    this.conflictNote = '';
    this.generatedOn = undefined;
    this.archivedOn = undefined;
    this.archivedById = undefined;
    this.requiresComment = false;
    this.requiresEvidence = false;
    this.normIds = undefined;
    this.effectiveNormIds = [];
    this.evidenceTasks = [];
    this.changeKind = undefined;
    this.oneTimeState = undefined;
    this.state = auditCriterionStateActive;
    this.externalId = '';
    this.columnName = '';
    this.commentTrailId = 0;
    this.auditTrailId = 0;
    this.created = undefined;
    this.createdById = undefined;
    this.modified = undefined;
    this.modifiedById = undefined;
  }

  // Whether the criterion is asked: the one reading of "in the active set" on the web.
  isAsked(): boolean {
    return this.state === auditCriterionStateActive;
  }

  clone(): AuditCriterion {
    const output = new AuditCriterion();
    output.auditCriterionId = this.auditCriterionId;
    output.criterionKey = this.criterionKey;
    output.lineageKey = this.lineageKey;
    output.requirement = this.requirement;
    output.successCriteria = this.successCriteria;
    output.required = this.required;
    output.mode = this.mode;
    output.verification = this.verification;
    output.sourceLinkId = this.sourceLinkId;
    output.sourceName = this.sourceName;
    output.sourceKind = this.sourceKind;
    output.sourceText = this.sourceText;
    output.conflictNote = this.conflictNote;
    output.generatedOn = this.generatedOn;
    output.archivedOn = this.archivedOn;
    output.archivedById = this.archivedById;
    output.requiresComment = this.requiresComment;
    output.requiresEvidence = this.requiresEvidence;
    output.normIds = this.normIds ? [...this.normIds] : undefined;
    output.effectiveNormIds = [...this.effectiveNormIds];
    output.evidenceTasks = this.evidenceTasks.map((task) => task.clone());
    output.changeKind = this.changeKind;
    output.oneTimeState = this.oneTimeState;
    output.state = this.state;
    output.externalId = this.externalId;
    output.columnName = this.columnName;
    output.commentTrailId = this.commentTrailId;
    output.auditTrailId = this.auditTrailId;
    output.created = this.created;
    output.createdById = this.createdById;
    output.modified = this.modified;
    output.modifiedById = this.modifiedById;

    return output;
  }

  // Drives the criteria editor's save-enable, so it compares the user-editable fields only. The
  // server-owned lineage, state, trails and stamps are deliberately left out (a state change is not a
  // save). The standards compare as sets (undefined and empty are the same thing: the server stores no
  // rows for either).
  isEqual(other: AuditCriterion): boolean {
    return (
      this.criterionKey === other.criterionKey &&
      this.externalId === other.externalId &&
      this.columnName === other.columnName &&
      this.requirement === other.requirement &&
      this.successCriteria === other.successCriteria &&
      this.required === other.required &&
      this.mode === other.mode &&
      this.verification === other.verification &&
      this.requiresComment === other.requiresComment &&
      this.requiresEvidence === other.requiresEvidence &&
      areNormIdsEqual(this.normIds, other.normIds)
    );
  }
}

export class EvidenceTaskBinding {
  taskId: number;

  taskName: string;

  mode: string;

  recurring: boolean;

  // The task is completed or archived, which is what verifies a one-time criterion.
  taskClosed: boolean;

  // The task was created by the criterion and lives nowhere else, so removing it here has to decide
  // whether it is deleted or becomes an ordinary task of the item.
  hidden: boolean;

  // How many criteria of this item hold this task: above one, removing it here leaves it with the
  // others and there is nothing to decide.
  criterionCount: number;

  // The AI advice on this task when someone asked for it: the raw EvidenceInspectionStatus name for
  // this criterion and for the task as a whole (shown through getAuditCheckStatusText), and its date.
  aiAdviceStatus: string | undefined;

  aiAdviceOverallStatus: string | undefined;

  aiAdviceOn: Date | undefined;

  constructor() {
    this.taskId = 0;
    this.taskName = '';
    this.mode = '';
    this.recurring = false;
    this.taskClosed = false;
    this.hidden = false;
    this.criterionCount = 1;
    this.aiAdviceStatus = undefined;
    this.aiAdviceOverallStatus = undefined;
    this.aiAdviceOn = undefined;
  }

  clone(): EvidenceTaskBinding {
    const output = new EvidenceTaskBinding();
    output.taskId = this.taskId;
    output.taskName = this.taskName;
    output.mode = this.mode;
    output.recurring = this.recurring;
    output.taskClosed = this.taskClosed;
    output.hidden = this.hidden;
    output.criterionCount = this.criterionCount;
    output.aiAdviceStatus = this.aiAdviceStatus;
    output.aiAdviceOverallStatus = this.aiAdviceOverallStatus;
    output.aiAdviceOn = this.aiAdviceOn;

    return output;
  }
}

// Criteria and evidence-task counts for one control of a standard, for the per-framework overview.
// A control without a criteria set is still reported (hasCriteria false) so every item shows its
// state. procedureCount is the route into the procedure-form editor.
export class NormCriteriaStatsRow {
  controlId: number;

  // False for a control with child items: criteria live on the leaf items only, so a parent row is a
  // grouping row without criteria or counts.
  isLeaf: boolean;

  hasCriteria: boolean;

  criteriaCount: number;

  oneTimeCriteriaCount: number;

  reviewed: boolean;

  // Live evidence tasks bound to the control's active one-time criteria.
  taskCount: number;

  // One-time criteria with at least one live evidence task.
  boundOneTimeCriteriaCount: number;

  // One-time criteria with at least one evidence task that a person completed or archived.
  verifiedOneTimeCriteriaCount: number;

  // Completed or archived evidence tasks whose AI advice for this control was not satisfied.
  overruledTaskCount: number;

  procedureCount: number;

  // A generation input changed after the last review: the set must be re-evaluated. The reason is a
  // code (getAuditCriteriaReviewReasonText), the date is when the oldest pending change happened.
  reviewRequiredOn: Date | undefined;

  reviewRequiredReason: string | undefined;

  needsReview: boolean;

  // The last re-evaluation by the AI, and how many criteria it changed since the last review.
  reevaluatedOn: Date | undefined;

  changesSinceReview: number;

  constructor() {
    this.controlId = 0;
    this.isLeaf = true;
    this.hasCriteria = false;
    this.criteriaCount = 0;
    this.oneTimeCriteriaCount = 0;
    this.reviewed = false;
    this.taskCount = 0;
    this.boundOneTimeCriteriaCount = 0;
    this.verifiedOneTimeCriteriaCount = 0;
    this.overruledTaskCount = 0;
    this.procedureCount = 0;
    this.reviewRequiredOn = undefined;
    this.reviewRequiredReason = undefined;
    this.needsReview = false;
    this.reevaluatedOn = undefined;
    this.changesSinceReview = 0;
  }

  // The AI re-evaluated the set and changed it, and nobody reviewed it since.
  isReevaluatedUnreviewed(): boolean {
    return this.hasCriteria && !this.reviewed && !!this.reevaluatedOn && this.changesSinceReview > 0;
  }

  clone(): NormCriteriaStatsRow {
    const output = new NormCriteriaStatsRow();
    output.controlId = this.controlId;
    output.isLeaf = this.isLeaf;
    output.hasCriteria = this.hasCriteria;
    output.criteriaCount = this.criteriaCount;
    output.oneTimeCriteriaCount = this.oneTimeCriteriaCount;
    output.reviewed = this.reviewed;
    output.taskCount = this.taskCount;
    output.boundOneTimeCriteriaCount = this.boundOneTimeCriteriaCount;
    output.verifiedOneTimeCriteriaCount = this.verifiedOneTimeCriteriaCount;
    output.overruledTaskCount = this.overruledTaskCount;
    output.procedureCount = this.procedureCount;
    output.reviewRequiredOn = this.reviewRequiredOn;
    output.reviewRequiredReason = this.reviewRequiredReason;
    output.needsReview = this.needsReview;
    output.reevaluatedOn = this.reevaluatedOn;
    output.changesSinceReview = this.changesSinceReview;

    return output;
  }
}

// The criteria sets a manager has to re-evaluate: the count and the oldest few, named. The
// dashboard widget's content; every item opens the Frameworks criteria pivot.
export class AuditCriteriaReviewSummary {
  count: number;

  items: AuditCriteriaReviewSummaryItem[];

  constructor() {
    this.count = 0;
    this.items = [];
  }
}

export class AuditCriteriaReviewSummaryItem {
  entityType: number;

  entityId: number;

  code: string;

  name: string;

  reviewRequiredOn: Date | undefined;

  reviewRequiredReason: string | undefined;

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
    this.code = '';
    this.name = '';
    this.reviewRequiredOn = undefined;
    this.reviewRequiredReason = undefined;
  }
}

// The whole-standard criteria generation run (TenantProcess.AuditCriteriaGeneration) as the Frameworks
// tab polls it: the run status, the counters the progress row renders and one row per item. Status
// values mirror the server's AuditCriteriaGenerationItemStatus.
export const auditCriteriaGenerationItemNotRun = 0;
export const auditCriteriaGenerationItemWaiting = 1;
export const auditCriteriaGenerationItemRunning = 2;
export const auditCriteriaGenerationItemReady = 3;
export const auditCriteriaGenerationItemFailed = 4;
export const auditCriteriaGenerationItemSkipped = 5;

export class AuditCriteriaGenerationStatus {
  normId: number;

  // TenantProcessStatus of the run: NotStarted when no run exists.
  runStatus: number;

  total: number;

  ready: number;

  generated: number;

  failed: number;

  // Items over the per-start cap, waiting for the next start.
  remaining: number;

  // Items prepared and queued or running in the current run.
  queued: number;

  // PLAN-075: items that read a generic set and get no draft of their own; counted in ready, never in generated.
  generic: number;

  items: AuditCriteriaGenerationItem[];

  constructor() {
    this.normId = 0;
    this.runStatus = 0;
    this.total = 0;
    this.ready = 0;
    this.generated = 0;
    this.failed = 0;
    this.remaining = 0;
    this.queued = 0;
    this.generic = 0;
    this.items = [];
  }

  isRunning(): boolean {
    return this.runStatus === 1;
  }
}

export class AuditCriteriaGenerationItem {
  entityType: number;

  entityId: number;

  code: string | undefined;

  name: string | undefined;

  status: number;

  criteriaCount: number | undefined;

  generated: boolean | undefined;

  // PLAN-075: true when the item reads a generic set and nothing was drafted for it.
  generic: boolean | undefined;

  error: string | undefined;

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
    this.code = undefined;
    this.name = undefined;
    this.status = auditCriteriaGenerationItemNotRun;
    this.criteriaCount = undefined;
    this.generated = undefined;
    this.generic = undefined;
    this.error = undefined;
  }
}

// The stored AI advice on one evidence task for one entity: the task's evidence judged against the
// entity's one-time criteria. Advice only; completing or archiving the task is what verifies them.
export class EvidenceTaskAdvice {
  taskId: number;

  taskName: string;

  generatedOn: Date | undefined;

  // Raw EvidenceInspectionStatus name of the task as a whole; shown through getAuditCheckStatusText.
  overallStatus: string;

  markdown: string;

  checks: EvidenceTaskAdviceCheck[];

  constructor() {
    this.taskId = 0;
    this.taskName = '';
    this.generatedOn = undefined;
    this.overallStatus = '';
    this.markdown = '';
    this.checks = [];
  }
}

export class EvidenceTaskAdviceCheck {
  criterionKey: string;

  requirement: string;

  status: string;

  summary: string;

  constructor() {
    this.criterionKey = '';
    this.requirement = '';
    this.status = '';
    this.summary = '';
  }
}

export class EvidenceTaskBindingRow {
  entityType: number;

  entityId: number;

  criterionKey: string;

  taskId: number;

  taskName: string;

  mode: string;

  // The task was created by the criterion path, and how many criteria of the item hold it: what the
  // panel needs to know before it offers to delete the task it just let go of.
  hidden: boolean;

  criterionCount: number;

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
    this.criterionKey = '';
    this.taskId = 0;
    this.taskName = '';
    this.mode = '';
    this.hidden = false;
    this.criterionCount = 1;
  }

  clone(): EvidenceTaskBindingRow {
    const output = new EvidenceTaskBindingRow();
    output.entityType = this.entityType;
    output.entityId = this.entityId;
    output.criterionKey = this.criterionKey;
    output.taskId = this.taskId;
    output.taskName = this.taskName;
    output.mode = this.mode;
    output.hidden = this.hidden;
    output.criterionCount = this.criterionCount;

    return output;
  }
}

// Existence + review state of an entity's criteria, for batch questions like the AI-audit start
// dialog.
export class AuditCriteriaStatus {
  entityType: number;

  entityId: number;

  // False for a control with child items, which carries no criteria.
  isLeaf: boolean;

  hasCriteria: boolean;

  reviewed: boolean;

  // PLAN-075: where the item's criteria come from
  layer: AuditCriteriaLayer;

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
    this.isLeaf = true;
    this.hasCriteria = false;
    this.reviewed = false;
    this.layer = AuditCriteriaLayer.None;
  }
}

export interface AuditEntityKey {
  entityType: number;
  entityId: number;
}

// Requirements share the control table and are audited exactly like a control, so their criteria
// live under the Control entity type. Every key built here folds the same way the server does.
export const normalizeAuditEntityType = (entityType: number): number =>
  entityType === (EntityTypes.Requirement as number) ? (EntityTypes.Control as number) : entityType;

// The key format the projected form fields carry: what groups a form's fields per audited entity.
export const auditEntityKeyString = (entityType: number, entityId: number): string =>
  `${normalizeAuditEntityType(entityType)}|${entityId}`;

// The audited entities a task covers, read from its context rows - the same families an
// internal-audit scope holds. This is what keys the criteria and evidence-task loads of a
// procedure or execution task.
export const getAuditEntityKeysOfTask = (task: {
  controlIds: number[];
  riskIds: number[];
  processIds: number[];
  assetIds: number[];
  objectiveIds: number[];
  processingActivityIds: number[];
}): AuditEntityKey[] => [
  ...task.controlIds.map((id) => ({ entityType: EntityTypes.Control as number, entityId: id })),
  ...task.riskIds.map((id) => ({ entityType: EntityTypes.Risk as number, entityId: id })),
  ...task.processIds.map((id) => ({ entityType: EntityTypes.Process as number, entityId: id })),
  ...task.assetIds.map((id) => ({ entityType: EntityTypes.Asset as number, entityId: id })),
  ...task.objectiveIds.map((id) => ({ entityType: EntityTypes.Objective as number, entityId: id })),
  ...task.processingActivityIds.map((id) => ({ entityType: EntityTypes.ProcessingActivity as number, entityId: id })),
];

// A library procedure covering an entity: the overview's route into the procedure-form editor.
export class AuditProcedureRef {
  taskId: number;

  name: string;

  constructor() {
    this.taskId = 0;
    this.name = '';
  }
}

// The criteria re-evaluations added, reworded and archived since the set's last review.
export class AuditCriteriaChanges {
  added: number;

  reworded: number;

  archived: number;

  constructor() {
    this.added = 0;
    this.reworded = 0;
    this.archived = 0;
  }

  total(): number {
    return this.added + this.reworded + this.archived;
  }

  clone(): AuditCriteriaChanges {
    const output = new AuditCriteriaChanges();
    output.added = this.added;
    output.reworded = this.reworded;
    output.archived = this.archived;

    return output;
  }
}

export default class AuditCriteriaSet {
  entityType: number;

  entityId: number;

  // False for a control with child items: criteria live on the leaf items only, and the server
  // refuses every write to a parent's set.
  isLeaf: boolean;

  hasCriteria: boolean;

  signature: string;

  outOfSync: boolean;

  generatedOn: Date | undefined;

  modifiedOn: Date | undefined;

  modifiedById: string | undefined;

  reviewedOn: Date | undefined;

  reviewedById: string | undefined;

  // A generation input changed after the last review (a standard activated, deactivated, removed or
  // upgraded, a requirement link, the control text, a related document). Server-owned: set by those
  // changes, cleared by the review confirmation and by a regeneration, kept by a plain save.
  reviewRequiredOn: Date | undefined;

  reviewRequiredReason: string | undefined;

  // The standards linked to the entity (a control's requirement links): the options a control
  // criterion can be tagged with and the default of a new one. Empty for the other families.
  linkedNormIds: number[];

  // The last re-evaluation by the AI and the reason that triggered it (a review reason code).
  // Server-owned.
  reevaluatedOn: Date | undefined;

  reevaluatedReason: string | undefined;

  // A re-evaluation is running for this set: the editor polls until it lands.
  reevaluating: boolean;

  // What re-evaluations changed since the last review.
  changes: AuditCriteriaChanges;

  criteria: AuditCriterion[];

  // PLAN-075, server-owned: where the criteria come from, the set's own id, the subtype of a subtype set, the
  // generic set a copy was made from and when, when that source last changed, and the set's form family with its
  // head version.
  layer: AuditCriteriaLayer;

  auditCriteriaSetId: number | undefined;

  subType: number | undefined;

  sourceSetId: number | undefined;

  copiedOn: Date | undefined;

  sourceChangedOn: Date | undefined;

  familyId: number | undefined;

  headTaskTypeId: number | undefined;

  headVersion: number | undefined;

  // PLAN-075 Task 14, server-owned: the latest AI outcome per criterion of the item (by lineage key)
  latestOutcomes: AICriterionOutcome[];

  constructor() {
    this.entityType = 0;
    this.entityId = 0;
    this.isLeaf = true;
    this.hasCriteria = false;
    this.signature = '';
    this.outOfSync = false;
    this.generatedOn = undefined;
    this.modifiedOn = undefined;
    this.modifiedById = undefined;
    this.reviewedOn = undefined;
    this.reviewedById = undefined;
    this.reviewRequiredOn = undefined;
    this.reviewRequiredReason = undefined;
    this.linkedNormIds = [];
    this.reevaluatedOn = undefined;
    this.reevaluatedReason = undefined;
    this.reevaluating = false;
    this.changes = new AuditCriteriaChanges();
    this.criteria = [];
    this.layer = AuditCriteriaLayer.None;
    this.auditCriteriaSetId = undefined;
    this.subType = undefined;
    this.sourceSetId = undefined;
    this.copiedOn = undefined;
    this.sourceChangedOn = undefined;
    this.familyId = undefined;
    this.headTaskTypeId = undefined;
    this.headVersion = undefined;
    this.latestOutcomes = [];
  }

  clone(): AuditCriteriaSet {
    const output = new AuditCriteriaSet();
    output.entityType = this.entityType;
    output.entityId = this.entityId;
    output.isLeaf = this.isLeaf;
    output.hasCriteria = this.hasCriteria;
    output.signature = this.signature;
    output.outOfSync = this.outOfSync;
    output.generatedOn = this.generatedOn;
    output.modifiedOn = this.modifiedOn;
    output.modifiedById = this.modifiedById;
    output.reviewedOn = this.reviewedOn;
    output.reviewedById = this.reviewedById;
    output.reviewRequiredOn = this.reviewRequiredOn;
    output.reviewRequiredReason = this.reviewRequiredReason;
    output.linkedNormIds = [...this.linkedNormIds];
    output.reevaluatedOn = this.reevaluatedOn;
    output.reevaluatedReason = this.reevaluatedReason;
    output.reevaluating = this.reevaluating;
    output.changes = this.changes.clone();
    output.criteria = this.criteria.map((criterion) => criterion.clone());
    output.layer = this.layer;
    output.auditCriteriaSetId = this.auditCriteriaSetId;
    output.subType = this.subType;
    output.sourceSetId = this.sourceSetId;
    output.copiedOn = this.copiedOn;
    output.sourceChangedOn = this.sourceChangedOn;
    output.familyId = this.familyId;
    output.headTaskTypeId = this.headTaskTypeId;
    output.headVersion = this.headVersion;
    output.latestOutcomes = this.latestOutcomes.map((outcome) => outcome.clone());

    return output;
  }

  // The AI re-evaluated the set after its last review and changed something: a person has to go
  // through the changes. A re-evaluation that changed nothing leaves a reviewed set reviewed.
  isReevaluatedSinceReview(): boolean {
    if (!this.reevaluatedOn || this.changes.total() === 0) return false;

    return !this.reviewedOn || this.reevaluatedOn.getTime() > this.reviewedOn.getTime();
  }

  // The set must be re-evaluated: a generation input changed after the last review confirmation (the
  // same rule the server applies, AuditCriteriaReview.NeedsReview). Independent of isReviewed: a set
  // can be reviewed AND flagged, which is what widens the bulk review queue without un-reviewing it.
  needsReview(): boolean {
    if (!this.reviewRequiredOn) return false;

    return !this.reviewedOn || this.reviewRequiredOn.getTime() > this.reviewedOn.getTime();
  }

  // The one reading of "these criteria have been reviewed", the same rule the server applies
  // (AuditCriteriaReview.IsReviewed): editing and reviewing are separate acts, so a review counts
  // only while it is at least as recent as the last change to the criteria. A set carrying no
  // modification stamp was written before the stamp existed - there is no known edit to be newer
  // than the review, so the review still counts.
  isReviewed(): boolean {
    if (!this.reviewedOn) return false;

    return !this.modifiedOn || this.reviewedOn.getTime() >= this.modifiedOn.getTime();
  }

  // Whether the evidence of this set is "green": every active one-time criterion has an evidence task
  // a person completed or archived. The same reading as the overview's Evidence column and the card
  // badge, and the same rule the server counts with (NormCriteriaStatsBuilder). Independent of
  // isReviewed: reviewing says the criteria are right, verifying says the evidence is in. Recurring
  // criteria are proven per audit and do not weigh in, so a set without one-time criteria is green.
  isEvidenceVerified(): boolean {
    return this.criteria
      .filter((criterion) => criterion.isAsked() && criterion.mode === auditCriterionModeOneTime)
      .every((criterion) =>
        criterion.evidenceTasks.some(
          (binding) => binding.mode === auditCriterionModeOneTime && isEvidenceTaskVerified(binding),
        ),
      );
  }

  isEqual(other: AuditCriteriaSet): boolean {
    if (this.criteria.length !== other.criteria.length) {
      return false;
    }

    return this.criteria.every((criterion, index) => criterion.isEqual(other.criteria[index]));
  }
}
